import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { faClose, faFileCirclePlus, faIdCard, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import ModalPortal from "../../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../../components/Animate/Animate"
import defaultStyle from "../../../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../../../../components/Titles/Titles"
import { ButtonIcon } from "../../../../components/Button/Button"
import InputField from "../../../../components/InputField/InputField"
import { PopupHelpButton } from "../../../../components/Help/PopupHelpButton"
import THEME from "../../../../configuration/Theme"
import { SIZE } from "../../../../configuration/Sizes"
import { CPF_DIGITS_LENGTH, isValidCpf, normalizeCpf } from "../../../../utils/documentFormat"
import { EPaymentType } from "../../../../Enum/EPaymentType"
import { GenericDropDown } from "../../../../components/Dropdown/Dropdown"
import { getFiscalPaymentTypeOptions } from "../../../../Objects/Order/paymentTypeOptions"

type Props = {
  isOpen: boolean
  isEnabled: boolean
  orderId: string
  consumerCpf: string
  paymentType: EPaymentType
  requiresPaymentMethod: boolean
  isOrderChecking: boolean
  onChange: (orderId: string) => void
  onConsumerCpfChange: (consumerCpf: string) => void
  onPaymentTypeChange: (paymentType: EPaymentType) => void
  onClose: () => void
  onEmit: () => void
}

export const FiscalManualEmissionPopup: React.FC<Props> = ({
  isOpen,
  isEnabled,
  orderId,
  consumerCpf,
  paymentType,
  requiresPaymentMethod,
  isOrderChecking,
  onChange,
  onConsumerCpfChange,
  onPaymentTypeChange,
  onClose,
  onEmit,
}) => {
  const { t } = useTranslation(["fiscalIntegration", "helpPopup"])

  const validOrderId = Number.isInteger(Number(orderId)) && Number(orderId) > 0

  const validConsumerCpf = consumerCpf.length === 0 || isValidCpf(consumerCpf)

  const validPaymentType = !requiresPaymentMethod || paymentType !== EPaymentType.None

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const focusTimeout = window.setTimeout(() => document.getElementById("fiscal-order-id")?.focus(), 350)
    return () => window.clearTimeout(focusTimeout)
  }, [isOpen])

  if (!isOpen) {
    return null
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              icon={faFileCirclePlus}
              iconColor={THEME.colors.iconBGColor}
              title={t("manual.title")}
            >
              <div className={`${defaultStyle.ButtonsActions} gap-1`}>
                <PopupHelpButton
                  variant="global"
                  helpKey="pages.myrestaurant.fiscalintegration.manualemission.1"
                  title={t("helpPopup:global.fiscalManualEmission.title")}
                  description={t("helpPopup:global.fiscalManualEmission.description")}
                  steps={t("helpPopup:global.fiscalManualEmission.steps", { returnObjects: true }) as string[]}
                  bullets={t("helpPopup:global.fiscalManualEmission.bullets", { returnObjects: true }) as string[]}
                />

                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalManualEmissionPopup.button.onClose"
                />
              </div>
            </TitlePageResponsiveIconSmall>

            <div className="space-y-4 p-3">
              <section className="rounded-xl border-2 border-emerald-100 bg-emerald-50 p-3 text-sm leading-relaxed text-emerald-900">
                <strong className="block">{t("manual.guideTitle")}</strong>

                <p className="mt-1 text-xs">{t("manual.description")}</p>
              </section>

              {!isEnabled && (
                <section className="flex gap-2 rounded-xl border-2 border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
                  <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5" />

                  <span>{t("manual.disabled")}</span>
                </section>
              )}

              <InputField
                id="fiscal-order-id"
                type="number"
                value={orderId}
                placeholder={t("manual.orderId")}
                onChange={(event) => onChange(event.target.value)}
                descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalManualEmissionPopup.input.orderId"
              />

              {requiresPaymentMethod && (
                <div className="space-y-1">
                  <GenericDropDown
                    legend={t("paymentMethod.label")}
                    initialState={paymentType.toString()}
                    data={[
                      {
                        value: EPaymentType.None.toString(),
                        label: t("paymentMethod.placeholder"),
                      },
                      ...getFiscalPaymentTypeOptions(),
                    ]}
                    handlerFuncion={(event) =>
                      onPaymentTypeChange(Number(event.target.value) as EPaymentType)
                    }
                    errorMessage={
                      paymentType === EPaymentType.None ? t("paymentMethod.required") : undefined
                    }
                    descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalManualEmissionPopup.select.paymentMethod"
                  />

                  <p className="px-1 text-xs leading-relaxed text-slate-600">
                    {t("paymentMethod.help")}
                  </p>
                </div>
              )}

              {isOrderChecking && (
                <p className="px-1 text-xs font-semibold text-slate-600">{t("manual.checkingOrder")}</p>
              )}

              <div className="space-y-1">
                <InputField
                  type="text"
                  icon={faIdCard}
                  value={consumerCpf}
                  inputMode="numeric"
                  maxLength={CPF_DIGITS_LENGTH}
                  pattern="\d{11}"
                  placeholder={t("manual.consumerCpf")}
                  onInvalidMessage={t("manual.consumerCpfInvalid")}
                  errorMessage={
                    consumerCpf.length > 0 && !validConsumerCpf ? t("manual.consumerCpfInvalid") : undefined
                  }
                  onChange={(event) => onConsumerCpfChange(normalizeCpf(event.target.value))}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalManualEmissionPopup.input.consumerCpf"
                />

                <p className="px-1 text-xs leading-relaxed text-slate-600">{t("manual.consumerCpfHelp")}</p>
              </div>

              <button
                type="button"
                onClick={onEmit}
                disabled={
                  !isEnabled ||
                  !validOrderId ||
                  !validConsumerCpf ||
                  !validPaymentType ||
                  isOrderChecking
                }
                className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalManualEmissionPopup.button.onEmit"
              >
                <FontAwesomeIcon icon={faFileCirclePlus} />

                {t("manual.emit")}
              </button>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
